import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { STAFF_ROLES } from '../../utils/roles';
import { useOutstandingCount } from '../../Pages/DailyOps/hooks/useOutstandingCount';
import './GlobalNav.css';

const NAV_LINKS = [
  { to: '/',        label: 'Home',            icon: '🏠', roles: ['customer','employee','manager','admin','owner'] },
  { to: '/FoodMenu',label: 'Menu',            icon: '🍽️', roles: ['customer','employee','manager','admin','owner'] },
  { to: '/Catering',label: 'Catering',        icon: '🎉', roles: ['customer','employee','manager','admin','owner'] },
  { to: '/catering-planner', label: 'Catering Planner', icon: '🍱', roles: ['manager','admin','owner'] },
  { to: '/daily-ops', label: 'Daily Operations',  icon: '📋', roles: ['employee','manager','admin','owner'] },
  { to: '/tickets',           label: 'Ticket Dashboard',    icon: '🎫', roles: ['manager','admin','owner'] },
  { to: '/square-sales',      label: 'Square Sales',        icon: '💰', roles: ['owner'] },
  { to: '/shifts',         label: 'Shift Dashboard', icon: '📊', roles: ['manager','admin','owner'] },
  // Staff-only while online ordering is being tested — widen roles to launch.
  { to: '/order',   label: 'Order Online (testing)', icon: '🛍️', roles: ['manager','admin','owner'] },
  { to: '/feedback',          label: 'Feedback & Complaints', icon: '💬', roles: ['customer','employee','manager','admin','owner'] },
  { to: '/kids',    label: 'Kids Corner 🌟',  icon: '🌟', roles: ['customer','employee','manager','admin','owner'] },
  { to: '/game',    label: 'Desi Bites Dash', icon: '🎮', roles: ['customer','employee','manager','admin','owner'] },
  { to: '/account', label: 'My Account',      icon: '👤', roles: ['employee','manager','admin','owner'] },
  { to: '/clock',   label: 'Clock In / Out',  icon: '⏱️', roles: ['employee','manager','admin','owner'] },
  { to: '/tasks',     label: 'Tasks',            icon: '✅', roles: ['employee','manager','admin','owner'] },
  { to: '/events',            label: 'Event Pre-Orders',   icon: '📝', roles: ['manager','admin','owner'] },
  { to: '/admin',             label: 'Manage Users',        icon: '🛠️', roles: ['admin','owner'] },
  { to: '/menu-manager',      label: 'Menu Manager',        icon: '🍽️', roles: ['manager','admin','owner'] },
  { to: '/order-status',      label: 'Order Status Board',  icon: '📺', roles: ['manager','admin','owner','security'] },
  { to: '/my-day',            label: 'My Day',              icon: '☀️', roles: ['owner'] },
  { to: '/expense-calculator', label: 'Expense Calculator',  icon: '💵', roles: ['owner'] },
  { to: '/sessions',          label: 'Sessions & Security', icon: '🔐', roles: ['owner'] },
  { to: '/storage-cleanup',   label: 'Storage Cleanup',     icon: '🗄️', roles: ['owner'] },
  { to: '/bot-config',        label: 'Bot Config',          icon: '⚙️', roles: ['owner'] },
  { to: '/login',   label: 'Login',           icon: '🔑', roles: ['customer'] },
  { to: '/signup',  label: 'Sign Up',         icon: '✨', roles: ['customer'] },
];

export default function GlobalNav() {
  const { user, role, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Prevent body scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const links = NAV_LINKS.filter(l => l.roles.includes(role || 'customer'));
  const initials = user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '?';
  const dailyOpsOutstanding = useOutstandingCount(STAFF_ROLES.includes(role));

  return (
    <>
      {/* Hamburger button — top right of page, always visible */}
      <button
        className={`gnav__hamburger${open ? ' gnav__hamburger--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Toggle navigation"
        aria-expanded={open}
      >
        <span /><span /><span />
      </button>

      {/* Backdrop */}
      {open && <div className="gnav__backdrop" onClick={() => setOpen(false)} aria-hidden="true" />}

      {/* Drawer */}
      <nav className={`gnav__drawer${open ? ' gnav__drawer--open' : ''}`} aria-hidden={!open}>
        {/* Drawer header */}
        <div className="gnav__drawer-header">
          <span className="gnav__drawer-logo">Desi Bites</span>
          <button className="gnav__drawer-close" onClick={() => setOpen(false)} aria-label="Close menu">
            <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" strokeWidth="2.5" fill="none">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* User info bar */}
        {user && (
          <div className="gnav__user">
            <span className="gnav__user-avatar">{initials}</span>
            <div className="gnav__user-info">
              <span className="gnav__user-name">{user.displayName || user.email}</span>
              <span className="gnav__user-role">{role}</span>
            </div>
          </div>
        )}

        {/* Links */}
        <ul className="gnav__list">
          {links.map(link => (
            <li key={link.to}>
              <Link
                to={link.to}
                className={`gnav__link${location.pathname === link.to ? ' gnav__link--active' : ''}`}
              >
                <span className="gnav__link-icon">{link.icon}</span>
                <span className="gnav__link-label">{link.label}</span>
                {link.to === '/daily-ops' && dailyOpsOutstanding > 0 && (
                  <span className="gnav__link-badge" title={`${dailyOpsOutstanding} item${dailyOpsOutstanding === 1 ? '' : 's'} need attention`}>
                    {dailyOpsOutstanding}
                  </span>
                )}
                {location.pathname === link.to && <span className="gnav__link-dot" />}
              </Link>
            </li>
          ))}
        </ul>

        {/* Footer */}
        <div className="gnav__footer">
          {user ? (
            <button className="gnav__logout" onClick={() => { logout(); setOpen(false); }}>
              <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/>
              </svg>
              Logout
            </button>
          ) : (
            <Link to="/login" className="gnav__login-btn" onClick={() => setOpen(false)}>
              🔑 Sign In
            </Link>
          )}
        </div>
      </nav>
    </>
  );
}
